import * as React from "react";

import { cn } from "@/lib/utils";

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-x-auto">
    <table
      ref={ref}
      className={cn(
        // min-w-max below sm is the whole mobile fix. With only `w-full` the
        // table compresses to the viewport, so columns get squeezed until
        // their contents shatter — a date cell became "29 / ago / 2026" over
        // three lines. Letting the table keep its natural width and scrolling
        // the wrapper instead keeps every row on one line and readable.
        // Restored to normal at sm+, where fitting the container is right.
        "w-full min-w-max caption-bottom text-sm sm:min-w-0",
        className
      )}
      {...props}
    />
  </div>
));
Table.displayName = "Table";

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
));
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props}
  />
));
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t border-[var(--fx-ink-100)] bg-[var(--fx-paper-2)] font-medium dark:border-[var(--dk-border)] dark:bg-[var(--dk-surface2)] [&>tr]:last:border-b-0",
      className
    )}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement> & {
    onRowClick?: (event: React.MouseEvent<HTMLTableRowElement>) => void;
  }
>(({ className, onRowClick, onClick, ...props }, ref) => {
  const handleClick = (event: React.MouseEvent<HTMLTableRowElement>): void => {
    const target = event.target as HTMLElement;
    const isInteractive =
      target.closest("button") !== null ||
      target.closest("a") !== null ||
      target.closest('[role="button"]') !== null ||
      target.closest("[data-interactive]") !== null ||
      target.closest('[role="menuitem"]') !== null ||
      target.closest("[data-radix-collection-item]") !== null;

    if (!isInteractive && onRowClick) {
      onRowClick(event);
    }

    onClick?.(event);
  };

  return (
    <tr
      ref={ref}
      className={cn(
        // Rows take colour only — no transform. Lifting a row shifts its
        // neighbours' perceived baseline and makes a long list feel unstable
        // as the pointer travels down it.
        "fx-interactive-row border-b border-[var(--fx-ink-100)] hover:bg-[var(--fx-s-raised-hover)] data-[state=selected]:bg-[var(--fx-s-selected)] dark:border-[var(--dk-border)] dark:hover:bg-[var(--fx-s-raised-hover)] dark:data-[state=selected]:bg-[var(--fx-s-selected)]",
        onRowClick && "cursor-pointer",
        className
      )}
      onClick={handleClick}
      {...props}
    />
  );
});
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      // Tighter gutters on a phone: 16px each side of every column is a lot
      // of a 390px viewport once the table is free to keep its own width.
      "h-12 px-3 text-left align-middle font-medium text-[var(--fx-ink-500)] sm:px-4 dark:text-[var(--dk-text-mute)] [&:has([role=checkbox])]:pr-0",
      className
    )}
    {...props}
  />
));
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn("px-3 py-4 align-middle sm:px-4 [&:has([role=checkbox])]:pr-0", className)}
    {...props}
  />
));
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-[var(--fx-ink-500)] dark:text-[var(--dk-text-mute)]", className)}
    {...props}
  />
));
TableCaption.displayName = "TableCaption";

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
